import React, { useCallback, useEffect, useState } from "react";
import { getUsers } from "../../lib/vikas";
import { useOpen } from "../../lib/beacon";

const SAVED = "Saved — applies at the next data build";
const LEVELS = [["none", "None"], ["view", "View"], ["edit", "Edit"]];
const nameOf = (p) => (typeof p === "string" ? p : p?.name || "");
// live shape: meta.rights[person].topics = {TOPIC: level}
const topicsOf = (r) => (r && typeof r.topics === "object" && r.topics ? r.topics : r || {});

// topics: every key in meta.rights, in board order first, then any others
function topicKeys(data) {
  const rights = data?.meta?.rights || {};
  const all = new Set(Object.values(rights).flatMap((r) => Object.keys(topicsOf(r))));
  const order = (data?.board?.sections || []).filter((s) => s.kind === "topic").map((s) => s.key);
  return [...order.filter((k) => all.has(k)), ...[...all].filter((k) => !order.includes(k))];
}

function AddPerson({ press }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const ok = name.trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const go = async (e) => {
    e.preventDefault();
    if (!ok) return;
    const id = await press("user_add", name.trim(), { kind: "user_add", name: name.trim(), email: email.trim().toLowerCase() });
    if (id) { setName(""); setEmail(""); }
  };
  return (
    <form className="users-add" onSubmit={go} data-testid="users-add-form">
      <input className="vk-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" aria-label="Name" data-testid="users-add-name" />
      <input className="vk-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Google email" aria-label="Google email" data-testid="users-add-email" />
      <button className="btn" type="submit" disabled={!ok} data-testid="users-add-btn">Add person</button>
    </form>
  );
}

function SigninList({ users }) {
  if (!users) return null;
  return (
    <div className="users-signin" data-testid="users-signin">
      <h2 className="users-h2">Who may sign in</h2>
      <ul>
        {users.map((u) => (
          <li key={u.email} className={u.active ? "" : "gone"} data-testid={`signin-${u.email}`}>
            <span className="nm">{u.name}</span> <span className="faint">{u.email}</span>{" "}
            <span className="label">{u.active ? (u.can_act ? "may act" : "view") : "deactivated"}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function UsersPage({ data, ctx }) {
  useOpen("users");
  const [users, setUsers] = useState(null);
  const load = useCallback(() => getUsers().then(setUsers).catch(() => setUsers(null)), []);
  useEffect(() => { load(); }, [load]);
  const [over, setOver] = useState({});
  const [gone, setGone] = useState({});
  const [ask, setAsk] = useState("");
  const people = (data?.meta?.people || []).map(nameOf).filter((n) => n && n !== "VIKAS");
  const rights = data?.meta?.rights || {};
  const topics = topicKeys(data);
  const press = async (kind, target, value) => {
    const id = await ctx.act("users", kind, target, JSON.stringify(value), "", kind === "rights" ? SAVED : "Saved — sign-in list changed now");
    if (id && kind !== "rights") load();
    return id;
  };
  const inactive = (p) => users && !users.some((u) => u.name === p && u.active) && users.some((u) => u.name === p);
  const setLevel = async (person, topic, level) => {
    const id = await press("rights", person, { kind: "rights", person, topic, level });
    if (id) setOver((o) => ({ ...o, [`${person}|${topic}`]: level }));
  };
  const deactivate = async (person) => {
    if (ask !== person) { setAsk(person); return; }
    setAsk("");
    const id = await press("user_deactivate", person, { kind: "user_deactivate", person });
    if (id) setGone((g) => ({ ...g, [person]: true }));
  };
  return (
    <div className="users-page" data-testid="users-page">
      <h1 className="users-h1" data-testid="users-title">Users</h1>
      <p className="faint users-note">Each change is one press in the queue. Rights apply at the next data build; Add person and Deactivate change who may sign in at once.</p>
      <AddPerson press={press} />
      <div className="users-scroll" data-testid="users-table-wrap">
        <table className="users-table" data-testid="users-table">
          <thead><tr><th scope="col">Person</th>{topics.map((t) => <th key={t} scope="col">{t}</th>)}<th scope="col"><span className="sr-only">Deactivate</span></th></tr></thead>
          <tbody>
            {people.map((p) => (
              <tr key={p} data-testid={`users-row-${p}`} className={gone[p] || inactive(p) ? "gone" : ""}>
                <th scope="row">{p}</th>
                {topics.map((t) => {
                  const k = `${p}|${t}`;
                  const v = over[k] || String(topicsOf(rights[p])[t] || "none").toLowerCase();
                  return (
                    <td key={t}>
                      <select className={`lvl lvl-${v}${over[k] ? " changed" : ""}`} value={v} aria-label={`${p} on ${t}`}
                        data-testid={`rights-${p}-${t}`} onChange={(e) => setLevel(p, t, e.target.value)}>
                        {LEVELS.map(([val, lab]) => <option key={val} value={val}>{lab}</option>)}
                      </select>
                    </td>
                  );
                })}
                <td>
                  {p === "Saket" || p === ctx.role ? null : gone[p] || inactive(p) ? <span className="faint small" data-testid={`users-gone-${p}`}>Deactivated</span>
                    : <button className={`btn tiny${ask === p ? "" : " ghost"}`} data-testid={`users-deactivate-${p}`} onClick={() => deactivate(p)} onBlur={() => setAsk("")}>{ask === p ? "Confirm — never deleted" : "Deactivate"}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <SigninList users={users} />
    </div>
  );
}
